import "./Materials.css";
import { useEffect, useRef,useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaEdit,
  FaArrowLeft,
  FaBoxOpen,
  FaWallet,
  FaChartPie,
  FaChartBar,
  FaPlus,
  FaFilePdf,
  FaTrash,
  FaChevronDown,
  FaChevronRight,
  FaTimes,
} from "react-icons/fa";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";


import { db } from "../../firebase/firebase";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  runTransaction,
} from "firebase/firestore";
import {
  useClientsStatusMap,
  resolveClientStatus,
  getProjectRef,
  PROJECT_REF_BADGE_STYLE,
} from "../../hooks/useClientsStatusMap";

import { useToast } from "./Invoicefiles/Usetoast";
import ToastContainer from "./Invoicefiles/Toastcontainer";
import usePagination from "../../hooks/usePagination";
import Pagination from "./Pagination";

// ===========================================================================
// MATERIALS PAGE — redesigned (Materials/Payments split).
//
// This page is now responsible ONLY for material/construction-expense
// information. Everything about client payments — Payment 1 / Payment 2 /
// Payment 3, payment history, and editing the Final Quotation — has moved
// to the new Payments page (see Payments.js). Invoice.js remains the single
// source of truth for RECORDING payments; this page only reads
// `invoiceRecord.amountPaid` for the "Remaining Payment" indicator below.
//
// Section A — Project Information: Client details, Original Estimation,
// Final Quotation (read-only here — edit it from Payments), and the
// Budget Allocation (the Material Breakdown from Estimation's Budget
// Planning). This section is unchanged in spirit from before.
//
// Section B — Daily Material Logs: a single, flat, CHRONOLOGICAL history
// of construction expenses (materials + labour + anything else logged),
// grouped visually into "Day 1", "Day 2", "Day 3"... by calendar date.
// There is no more per-payment grouping, no waterfall allocation engine,
// and no "Active Payment" of any kind — every log is simply appended to
// one running list, `paymentAllocations/{invoiceId}.materialEntries`.
//
// Section C — Remaining Payment indicator: Amount Paid (from Invoice) minus
// Total Logged Expense. This is allowed to go NEGATIVE — logging an entry
// is never blocked. A negative remaining payment just means expenses have
// outpaced what the client has paid so far; it is shown in red as a
// warning, not an error.
//
// Section D — Analytics ("View Chart"): a read-only modal showing a Pie
// chart (material-wise expenditure distribution) and a Bar chart (daily
// expenditure), both built purely from the same materialEntries/
// dailyLogGroups already computed for Section B, plus a dedicated
// "Generate PDF" that produces a full Project Cost & Analytics Report
// with the actual chart images embedded.
// ---------------------------------------------------------------------

const EMPTY_ITEM_FORM = {
  name: "",
  pricePerQty: "",
  quantity: "",
  notes: "",
};

const EMPTY_ENTRY_FORM = {
  name: "",
  quantity: "",
  unitPrice: "",
  notes: "",
  entryDate: new Date().toISOString().slice(0, 10),
};

// ---------------------------------------------------------------------
// Legacy-shape migration helpers — kept from the old Payment-Allocation
// era purely so historical data (entries saved under the removed
// Auto/Manual/Pointer system, or rounds logged via Invoice.js's
// "Log Construction Expense" modal) still shows up correctly here. None
// of this ever decides "which payment funds this log" anymore — that
// entire concept has been removed. Every entry is simply one line in one
// flat chronological list.
// ---------------------------------------------------------------------

function buildLegacySignature(name, quantity, unitPrice, dateVal) {
  return [
    (name || "").trim().toLowerCase(),
    Number(quantity) || 0,
    Number(unitPrice) || 0,
    dateVal || "",
  ].join("|");
}

// Cleans up entries duplicated by an earlier bug (re-importing the same
// Invoice.js "allocations" round on every reload). Keeps the OLDEST copy
// of each duplicate invoice-sourced entry and drops the rest. Entries
// logged directly on this page are never touched.
function dedupeLegacyEntries(entries) {
  const seen = new Set();
  const deduped = [];
  const sorted = [...entries].sort(
    (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
  );

  sorted.forEach((entry) => {
    if (entry.source !== "invoice") {
      deduped.push(entry);
      return;
    }
    const sig = buildLegacySignature(entry.name, entry.quantity, entry.unitPrice, entry.entryDate);
    if (seen.has(sig)) return;
    seen.add(sig);
    deduped.push(entry);
  });

  return deduped;
}

// One-time, self-healing migration: strips any entry still carrying the
// removed `allocationMode` / `manualTargetSeq` / `fixedAllocations` fields
// from the old Auto/Manual/Pointer system down to the current, simpler
// shape. Only the item details and the `source` display-badge survive.
function migrateLegacyEntryShape(entries) {
  let changed = false;
  const migrated = entries.map((entry) => {
    const hasLegacyFields =
      "allocationMode" in entry || "manualTargetSeq" in entry || "fixedAllocations" in entry;

    if (!hasLegacyFields) return entry;

    changed = true;
    const { allocationMode, manualTargetSeq, fixedAllocations, untaggedFallback, ...rest } = entry;

    return {
      ...rest,
      source: allocationMode === "invoice" || allocationMode === "legacy" ? "invoice" : "materials",
    };
  });
  return { migrated, changed };
}

// Continuous sync: Invoice.js's "Log Construction Expense" modal appends
// rounds into `paymentAllocations/{invoiceId}.allocations` independently
// of this page's `materialEntries` log. This treats `allocations` as an
// ever-growing feed and folds any not-yet-represented round into the flat
// Daily Material Logs list, tagged `source: "invoice"` purely as a display
// badge — it is funded/logged exactly like every other entry.
function syncLegacyAllocationRounds(existingEntries, legacyRounds) {
  if (!Array.isArray(legacyRounds) || legacyRounds.length === 0) {
    return [];
  }

  const alreadySyncedIds = new Set(
    existingEntries
      .map((e) => e.sourceAllocationId)
      .filter((id) => id !== undefined && id !== null),
  );

  const existingSignatures = new Set(
    existingEntries
      .filter((e) => e.source === "invoice")
      .map((e) => buildLegacySignature(e.name, e.quantity, e.unitPrice, e.entryDate)),
  );

  const newRounds = legacyRounds.filter((round) => {
    if (round.id && alreadySyncedIds.has(round.id)) return false;

    const items = round.items || [];
    if (items.length === 0) return true;

    const allItemsAlreadyPresent = items.every((item) =>
      existingSignatures.has(
        buildLegacySignature(item.name, item.quantity, item.pricePerQty, round.createdAt),
      ),
    );
    return !allItemsAlreadyPresent;
  });

  if (newRounds.length === 0) return [];

  const sortedNewRounds = [...newRounds].sort(
    (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
  );

  const entries = [];

  sortedNewRounds.forEach((round) => {
    (round.items || []).forEach((item, idx) => {
      const totalCost = Number(item.total) || 0;
      entries.push({
        id: `legacy-${round.id || "round"}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
        name: item.name || "Untitled material",
        quantity: Number(item.quantity) || 0,
        unit: item.unit || "",
        unitPrice: Number(item.pricePerQty) || 0,
        totalCost,
        supplier: item.supplier || "",
        notes: item.notes || "",
        entryDate: round.createdAt || "",
        source: "invoice",
        sourceAllocationId: round.id ?? null,
        createdAt: round.createdAt || new Date(0).toISOString(),
      });
    });
  });

  return entries;
}

// =====================================================================
// Material Expense Report (PDF) — read-only export.
//
// Presentation-only: built straight from data this page has already
// fetched/computed (planItems, sortedEntries, totalActualPurchased,
// invoiceRecord). No per-payment breakdown anymore — just Budget
// Allocation + the full chronological Daily Material Logs + a summary.
// =====================================================================

const COMPANY_NAME = "Sarrathi Constructions";
const COMPANY_ADDRESS =
  "82, Jagannathan Salai, Anjugam Nagar,SRP Colony, Kolathur, Chennai - 600082.";
const LOGO_PATH = "/logo/company logo.jpeg";
const COMPANY_EMAIL = "build@sarrathiconstructions.com";
const COMPANY_PHONE = "98405 24873, 98407 98059";
const COMPANY_WEBSITE = "www.sarrathiconstructions.com";

const PDF_THEME = {
  colors: {
    primaryDark: [23, 32, 51],
    primary: [22, 37, 61],
    accentGold: [199, 156, 68],
    textMuted: [110, 118, 132],
    textBody: [55, 65, 81],
    borderLight: [225, 228, 233],
    rowStripe: [246, 247, 250],
    balanceRed: [196, 78, 66],
    paidGreen: [46, 138, 89],
  },
  fontFamily: "helvetica",
};

const loadCompanyLogoDataUrl = async () => {
  try {
    const res = await fetch(LOGO_PATH);
    if (!res.ok) throw new Error("Logo not found");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("Failed to load company logo for PDF:", err);
    return null;
  }
};

const sanitizeFileName = (name) => {
  const cleaned = (name || "").trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return cleaned || "Client";
};

const formatRupeesPdf = (value) => {
  const num = Number(value);
  if (!value || Number.isNaN(num)) return "Rs. 0";
  return `Rs. ${num.toLocaleString("en-IN")}`;
};

const drawSectionTitle = (docPdf, text, x, y) => {
  const { colors } = PDF_THEME;
  docPdf.setFont(PDF_THEME.fontFamily, "bold");
  docPdf.setFontSize(11.5);
  docPdf.setTextColor(...colors.primaryDark);
  docPdf.text(text.toUpperCase(), x, y);
  docPdf.setDrawColor(...colors.accentGold);
  docPdf.setLineWidth(1.1);
  docPdf.line(x, y + 5, x + 34, y + 5);
  return y + 22;
};

const baseTableOptions = () => ({
  theme: "grid",
  styles: {
    font: PDF_THEME.fontFamily,
    fontSize: 9,
    cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
    textColor: PDF_THEME.colors.textBody,
    lineColor: PDF_THEME.colors.borderLight,
    lineWidth: 0.6,
    valign: "middle",
  },
  headStyles: {
    fillColor: PDF_THEME.colors.primary,
    textColor: 255,
    fontStyle: "bold",
    fontSize: 9,
    halign: "left",
  },
  alternateRowStyles: { fillColor: PDF_THEME.colors.rowStripe },
});

const PDF_HEADER_BAND = 74;

// Shared color palette — used for both the on-screen Pie/Bar charts and
// the PDF's material-breakdown color swatches, so the two always match.
// A refined, jewel-tone palette (gold, deep teal, burgundy, plum,
// bronze...) instead of flat/bright primary colors — used for both the
// on-screen Pie chart and the PDF's material-breakdown color swatches,
// so the two always match.
// Royal, jewel-rich palette — deep purple, emerald, sapphire, ruby,
// gold, amethyst... bold and regal rather than muted or flat/primary.
// Shared by the on-screen Pie chart AND the PDF's material-breakdown
// color swatches, so the two always match.
// const CHART_COLORS = [
//   "#8414d3", // royal purple
//   "#B8860B", // royal gold
//   "#0B4F8A", // sapphire blue
//   "#8B0000", // ruby red
//   "#0E6B4F", // emerald green
//   "#9B1B6B", // royal magenta
//   "#a142e6", // deep indigo
//   "#B22234", // crimson
//   "#1F6F78", // royal teal
//   "#7C2D91", // amethyst
// ];

const CHART_COLORS = [
  "#0B3954", // navy
  "#BFA181", // champagne gold
  "#087E8B", // teal
  "#FF5A5F", // coral
  "#3D315B", // deep plum
  "#C6AC8F", // beige gold
  "#1F4287", // royal blue
  "#A63446", // brick red
  "#41521F", // olive
  "#7A6C5D", // warm grey
];
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);
function Materials() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const clientDocId = searchParams.get("clientDocId") || "";
  const invoiceIdParam = searchParams.get("invoiceId") || "";
  const clientId = searchParams.get("clientId") || "";
  const clientName = searchParams.get("clientName") || "";
  const projectType = searchParams.get("projectType") || "";

  const { clientsById, clientsLoading } = useClientsStatusMap();
  const { toasts, toast } = useToast();

  // ---------------- Project picker (when no clientDocId in URL) ----------------
  const [budgetPlanEntries, setBudgetPlanEntries] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(true);
  const [pickerSearch, setPickerSearch] = useState("");

  const fetchBudgetPlansForPicker = async () => {
    setPickerLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "budgetPlans"));
      const list = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (Array.isArray(data.items) && data.items.length > 0) {
          list.push({ clientDocId: docSnap.id, ...data });
        }
      });

      list.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bTime - aTime;
      });

      setBudgetPlanEntries(list);
    } catch (err) {
      console.error("Failed to load budget plans:", err);
    } finally {
      setPickerLoading(false);
    }
  };

  useEffect(() => {
    if (!clientDocId) fetchBudgetPlansForPicker();
  }, [clientDocId]);

  const { validPickerRows, orphanBudgetPlanIds } = useMemo(() => {
    if (clientsLoading) {
      return { validPickerRows: [], orphanBudgetPlanIds: [] };
    }

    const valid = [];
    const orphanIds = [];

    budgetPlanEntries.forEach((plan) => {
      const c = clientsById[plan.clientDocId];
      const isValidClient =
        !!c &&
        c.id === plan.clientDocId &&
        !!(c.name || "").trim() &&
        !!(c.clientId || "").trim();

      if (isValidClient) {
        valid.push({
          clientDocId: plan.clientDocId,
          clientId: c.clientId || "",
          name: c.name || "",
          projectType: c.projectType || "",
          status: c.status || "In Talk",
          totalPlanned: plan.totalPlanned,
        });
      } else {
        orphanIds.push(plan.clientDocId);
      }
    });

    return { validPickerRows: valid, orphanBudgetPlanIds: orphanIds };
  }, [budgetPlanEntries, clientsById, clientsLoading]);

  useEffect(() => {
    if (clientsLoading || orphanBudgetPlanIds.length === 0) return;

    orphanBudgetPlanIds.forEach((id) => {
      deleteDoc(doc(db, "budgetPlans", id)).catch((err) =>
        console.error(`Failed to clean up orphan budget plan ${id}:`, err),
      );
    });
  }, [clientsLoading, orphanBudgetPlanIds]);

  const filteredPickerRows = useMemo(() => {
    const term = pickerSearch.trim().toLowerCase();
    if (!term) return validPickerRows;
    return validPickerRows.filter((row) => {
      const id = (row.clientId || "").toLowerCase();
      const name = (row.name || "").toLowerCase();
      return id.includes(term) || name.includes(term);
    });
  }, [pickerSearch, validPickerRows]);

  const {
    pageData: paginatedPickerRows,
    ...pickerPagination
  } = usePagination(filteredPickerRows, { resetKey: pickerSearch });

  const openMaterialsForClient = (row) => {
    const params = new URLSearchParams({
      clientDocId: row.clientDocId,
      clientId: row.clientId || "",
      clientName: row.name || "",
      projectType: row.projectType || "",
    });
    navigate(`/dashboard/materials?${params.toString()}`);
  };

  // ---------------- Budget plan (Section: Material Items) ----------------
  const [budgetPlan, setBudgetPlan] = useState(null);
  const [budgetPlanLoading, setBudgetPlanLoading] = useState(true);

  const fetchBudgetPlan = async () => {
    if (!clientDocId) {
      setBudgetPlanLoading(false);
      return;
    }
    setBudgetPlanLoading(true);
    try {
      const snap = await getDoc(doc(db, "budgetPlans", clientDocId));
      setBudgetPlan(snap.exists() ? snap.data() : null);
    } catch (err) {
      console.error("Failed to load budget plan:", err);
    } finally {
      setBudgetPlanLoading(false);
    }
  };

  // ---------------- Estimation record (Estimated Quotation) ----------------
  const [estimationRecord, setEstimationRecord] = useState(null);
  const [estimationLoading, setEstimationLoading] = useState(true);

  const fetchEstimationRecord = async () => {
    if (!clientDocId) {
      setEstimationLoading(false);
      return;
    }
    setEstimationLoading(true);
    try {
      const q = query(collection(db, "estimations"), where("clientDocId", "==", clientDocId));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        setEstimationRecord({ id: d.id, ...d.data() });
      } else {
        setEstimationRecord(null);
      }
    } catch (err) {
      console.error("Failed to load estimation record:", err);
    } finally {
      setEstimationLoading(false);
    }
  };

  // ---------------- Invoice record (optional — only exists post-Invoice) ----------------
  // Materials only ever READS `amountPaid` / `finalEstimation` from here —
  // it never edits the Final Quotation or records a payment. Both of those
  // now live exclusively on the Payments page.
  const [invoiceRecord, setInvoiceRecord] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(true);

  const fetchInvoiceRecord = async () => {
    if (!clientDocId) {
      setInvoiceLoading(false);
      return;
    }
    setInvoiceLoading(true);
    try {
      let resolved = null;

      if (invoiceIdParam) {
        const snap = await getDoc(doc(db, "invoices", invoiceIdParam));
        if (snap.exists()) resolved = { id: snap.id, ...snap.data() };
      }

      if (!resolved) {
        const q = query(collection(db, "invoices"), where("clientDocId", "==", clientDocId));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          resolved = { id: d.id, ...d.data() };
        }
      }

      setInvoiceRecord(resolved);
    } catch (err) {
      console.error("Failed to load invoice:", err);
    } finally {
      setInvoiceLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgetPlan();
    fetchEstimationRecord();
    fetchInvoiceRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientDocId, invoiceIdParam]);

  // ---------------- Daily Material Logs ----------------
  const [materialEntries, setMaterialEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(true);

  const fetchMaterialEntries = async () => {
    const targetInvoiceId = invoiceRecord?.id;
    if (!targetInvoiceId) {
      setMaterialEntries([]);
      setEntriesLoading(false);
      return;
    }
    setEntriesLoading(true);
    try {
      const snap = await getDoc(doc(db, "paymentAllocations", targetInvoiceId));

      if (!snap.exists()) {
        setMaterialEntries([]);
        return;
      }

      const data = snap.data();
      const rawEntries = Array.isArray(data.materialEntries) ? data.materialEntries : [];

      const { migrated: migratedEntries, changed: migrationChanged } =
        migrateLegacyEntryShape(rawEntries);

      const existingEntries = dedupeLegacyEntries(migratedEntries);

      const legacyRounds = Array.isArray(data.allocations) ? data.allocations : [];
      const newlySyncedEntries = syncLegacyAllocationRounds(existingEntries, legacyRounds);

      const merged =
        newlySyncedEntries.length > 0 ? [...existingEntries, ...newlySyncedEntries] : existingEntries;

      setMaterialEntries(merged);

      if (migrationChanged || merged.length !== rawEntries.length || newlySyncedEntries.length > 0) {
        setDoc(
          doc(db, "paymentAllocations", targetInvoiceId),
          { materialEntries: merged, updatedAt: serverTimestamp() },
          { merge: true },
        ).catch((err) => console.error("Failed to persist cleaned-up material entries:", err));
      }
    } catch (err) {
      console.error("Failed to load material entries:", err);
    } finally {
      setEntriesLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterialEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceRecord?.id]);

  const formatRupees = (value) => {
    const num = Number(value);
    if (!value || Number.isNaN(num)) return "₹ 0";
    return `₹ ${num.toLocaleString("en-IN")}`;
  };

  const formatSignedRupees = (value) => {
    const num = Number(value) || 0;
    const abs = Math.abs(num);
    const formatted = `₹ ${abs.toLocaleString("en-IN")}`;
    return num < 0 ? `-${formatted}` : formatted;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      const dateObj = value?.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(dateObj.getTime())) return "-";
      return dateObj.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  // ---------------- Quotation figures ----------------
  const estimatedQuotation =
    estimationRecord?.estimationAmount ?? budgetPlan?.quotationAmount ?? 0;
  const finalQuotation = invoiceRecord?.finalEstimation;
  const hasFinalQuotation = finalQuotation !== undefined && finalQuotation !== null;
  const activeQuotation = hasFinalQuotation ? finalQuotation : estimatedQuotation;
  const amountPaid = Number(invoiceRecord?.amountPaid) || 0;

  const plannedTotal = budgetPlan?.totalPlanned ?? 0;
  const plannedRemaining = activeQuotation - plannedTotal;
  const planItems = budgetPlan?.items || [];

  // ---------------- Daily Material Logs — flat chronological history ----------------
  // No more waterfall/allocation engine, no more "Active Payment" of any
  // kind. Every log is simply one line in one running list, grouped for
  // display into "Day 1", "Day 2"... by calendar date (ascending).
  const sortedEntries = useMemo(
    () =>
      [...materialEntries].sort(
        (a, b) =>
          new Date(a.entryDate || a.createdAt || 0) - new Date(b.entryDate || b.createdAt || 0) ||
          new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      ),
    [materialEntries],
  );

  const dailyLogGroups = useMemo(() => {
    const byDate = new Map();
    sortedEntries.forEach((entry) => {
      const key = entry.entryDate || "no-date";
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key).push(entry);
    });

    let dayNumber = 0;
    return Array.from(byDate.entries()).map(([date, items]) => {
      dayNumber += 1;
      return { dayNumber, date, items };
    });
  }, [sortedEntries]);

  // ---------------- Day expand/collapse (independent per day) ----------------
  // Keyed by the day's date string. A day is expanded only when its key
  // is explicitly set to true — the initial default (only the latest day
  // expanded, all others collapsed) is applied once by the effect below;
  // every subsequent expand/collapse remains fully manual via
  // toggleDayExpanded, unaffected by this default.
  const [expandedDays, setExpandedDays] = useState({});
  const hasSetDefaultExpandedDayRef = useRef(false);

  const isDayExpanded = (date) => expandedDays[date] === true;

  const toggleDayExpanded = (date) => {
    setExpandedDays((prev) => ({ ...prev, [date]: !isDayExpanded(date) }));
  };

  // On initial load of the logs for a given project, expand only the
  // latest day (last entry in dailyLogGroups, since it's sorted
  // ascending by date) and leave every earlier day collapsed. Runs once
  // per client (guarded by the ref, reset when switching projects) so it
  // never overrides a user's manual expand/collapse afterward.
  useEffect(() => {
    hasSetDefaultExpandedDayRef.current = false;
    setExpandedDays({});
  }, [clientDocId]);

  useEffect(() => {
    if (hasSetDefaultExpandedDayRef.current) return;
    if (dailyLogGroups.length === 0) return;

    const latestDay = dailyLogGroups[dailyLogGroups.length - 1];
    setExpandedDays({ [latestDay.date]: true });
    hasSetDefaultExpandedDayRef.current = true;
  }, [dailyLogGroups]);

  // ---------------- Budget vs Actual / Remaining Payment ----------------
const totalActualPurchased = useMemo(
    () => materialEntries.reduce((sum, entry) => sum + (Number(entry.totalCost) || 0), 0),
    [materialEntries],
  );

  // Remaining Payment = what the client has paid so far, minus what has
  // been logged as spent. This is allowed to go NEGATIVE — logging is
  // never blocked. A negative value simply flags that expenses have
  // outpaced payments received, shown in red via the existing
  // "over-budget" style.
  const remainingPayment = amountPaid - totalActualPurchased;

  // =====================================================================
  // Analytics — Material-wise totals (pie chart) + Daily totals (bar
  // chart). Built purely from `materialEntries` / `dailyLogGroups`, both
  // already scoped to THIS project's single invoiceRecord.id — so this
  // can never mix in another project's data, even for a client with
  // multiple projects.
  // =====================================================================

  // Same material logged across several Daily Logs is combined into ONE
  // total before any percentage is calculated (never treated as separate
  // slices) — e.g. Sand on Day 1 + Sand on Day 2 + Sand on Day 3 = one
  // "Sand" total.
  const materialTotals = useMemo(() => {
    const map = new Map();
    materialEntries.forEach((entry) => {
      const name = (entry.name || "Unnamed").trim() || "Unnamed";
      const cost = Number(entry.totalCost) || 0;
      map.set(name, (map.get(name) || 0) + cost);
    });
    return Array.from(map.entries())
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
  }, [materialEntries]);

  // Total Material Expenditure = sum of every Daily Log total (same as
  // totalActualPurchased above — kept as its own named value here purely
  // for readability in the analytics/PDF code below).
  const totalMaterialExpenditure = totalActualPurchased;

  const materialTotalsWithPct = useMemo(
    () =>
      materialTotals.map((m, idx) => ({
        ...m,
        pct: totalMaterialExpenditure > 0 ? (m.total / totalMaterialExpenditure) * 100 : 0,
        color: CHART_COLORS[idx % CHART_COLORS.length],
      })),
    [materialTotals, totalMaterialExpenditure],
  );

  // Daily totals — reuses the same dailyLogGroups already computed above
  // for the on-screen Daily Material Logs section, so the bar chart can
  // never disagree with what's shown there.
  const dailyTotals = useMemo(
    () =>
      dailyLogGroups.map((group) => ({
        dayNumber: group.dayNumber,
        date: group.date,
        total: group.items.reduce((sum, e) => sum + (Number(e.totalCost) || 0), 0),
        items: group.items,
      })),
    [dailyLogGroups],
  );

  const pieChartData = useMemo(
    () => ({
      labels: materialTotalsWithPct.map((m) => m.name),
      datasets: [
        {
          data: materialTotalsWithPct.map((m) => m.total),
          backgroundColor: materialTotalsWithPct.map((m) => m.color),
          hoverBackgroundColor: materialTotalsWithPct.map((m) => m.color),
          hoverBorderColor: "#ffffff",
          hoverBorderWidth: 3,
          borderColor: "#ffffff",
          borderWidth: 2,
        },
      ],
    }),
    [materialTotalsWithPct],
  );

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 10 }, padding: 10 } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const m = materialTotalsWithPct[ctx.dataIndex];
            return ` ${m.name}: ${formatRupees(m.total)} (${m.pct.toFixed(1)}%)`;
          },
        },
      },
    },
  };

  const barChartData = useMemo(
    () => ({
      labels: dailyTotals.map((d) => `Day ${d.dayNumber}`),
      datasets: [
        {
          label: "Daily Expenditure",
          data: dailyTotals.map((d) => d.total),
          backgroundColor: dailyTotals.map((_, idx) => CHART_COLORS[idx % CHART_COLORS.length]),
          hoverBackgroundColor: dailyTotals.map((_, idx) => CHART_COLORS[idx % CHART_COLORS.length]),
          borderRadius: 6,
          barPercentage: 0.45,
          categoryPercentage: 0.6,
        },
      ],
    }),
    [dailyTotals],
  );

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${formatRupees(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (val) => `₹${Number(val).toLocaleString("en-IN")}` },
      },
    },
  };

  // ---------------- View Chart modal ----------------
  const [showChartModal, setShowChartModal] = useState(false);
  const pieChartRef = useRef(null);
  const barChartRef = useRef(null);

  // =====================================================================
  // Generate PDF — Material Expense Report.
  // =====================================================================
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const generateMaterialsPDF = async () => {
    if (!invoiceRecord) return;

    setPdfGenerating(true);
    try {
      const { colors } = PDF_THEME;
      const docPdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = docPdf.internal.pageSize.getWidth();
      const marginX = 40;

      const logoDataUrl = await loadCompanyLogoDataUrl();
      const headerTop = 40;
      const logoSize = 54;

      if (logoDataUrl) {
        try {
          docPdf.addImage(logoDataUrl, "JPEG", marginX, headerTop, logoSize, logoSize);
        } catch (imgErr) {
          console.error("Failed to draw logo on materials report PDF:", imgErr);
        }
      }

      const textX = marginX + logoSize + 16;
      const nameY = headerTop + logoSize / 2 - 6;

      docPdf.setFont(PDF_THEME.fontFamily, "bold");
      docPdf.setFontSize(17);
      docPdf.setTextColor(...colors.primaryDark);
      docPdf.text(COMPANY_NAME, textX, nameY);

      docPdf.setFont(PDF_THEME.fontFamily, "normal");
      docPdf.setFontSize(9);
      docPdf.setTextColor(...colors.textMuted);
           docPdf.text(COMPANY_ADDRESS, textX, nameY + 15, {
        maxWidth: pageWidth - marginX * 2 - logoSize - 16,
      });
      docPdf.text(`Phone: ${COMPANY_PHONE}`, textX, nameY + 27);
      docPdf.text(`Email: ${COMPANY_EMAIL}`, textX, nameY + 39);
      docPdf.text(`Website: ${COMPANY_WEBSITE}`, textX, nameY + 51);

      const logoRowBottom = Math.max(headerTop + logoSize, nameY + 51) + 16;
      docPdf.setDrawColor(...colors.accentGold);
      docPdf.setLineWidth(1.3);
      docPdf.line(marginX, logoRowBottom, pageWidth - marginX, logoRowBottom);

      const titleY = logoRowBottom + 26;

      docPdf.setFont(PDF_THEME.fontFamily, "bold");
      docPdf.setFontSize(15);
      docPdf.setTextColor(...colors.primaryDark);
      docPdf.text("MATERIAL EXPENSE REPORT", marginX, titleY);

      docPdf.setFont(PDF_THEME.fontFamily, "normal");
      docPdf.setFontSize(9.5);
      docPdf.setTextColor(...colors.textMuted);
      docPdf.text(
        `Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`,
        pageWidth - marginX,
        titleY,
        { align: "right" },
      );

      const headerBottom = titleY + 16;
      docPdf.setDrawColor(...colors.borderLight);
      docPdf.setLineWidth(0.8);
      docPdf.line(marginX, headerBottom, pageWidth - marginX, headerBottom);

      let cursorY = headerBottom + 22;

      cursorY = drawSectionTitle(docPdf, "Client Information", marginX, cursorY);

      autoTable(docPdf, {
        ...baseTableOptions(),
        startY: cursorY,
        showHead: false,
        styles: { ...baseTableOptions().styles, fontSize: 9.5 },
        body: [
          ["Client Name", clientName || "-", "Client ID", clientId || "-"],
          ["Project Type", projectType || "-", "Final Quotation", formatRupeesPdf(activeQuotation)],
        ],
        columnStyles: {
          0: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 90 },
          1: { cellWidth: 195 },
          2: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 90 },
          3: { cellWidth: "auto" },
        },
        margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
      });

      cursorY = docPdf.lastAutoTable.finalY + 26;

      // ---- Budget Allocation ----
      cursorY = drawSectionTitle(docPdf, "Budget Allocation", marginX, cursorY);

      const budgetRows = planItems.map((item) => {
        const qty = item.quantity ?? 1;
        const pricePerQty =
          item.pricePerQty ?? (qty ? (Number(item.budget) || 0) / qty : Number(item.budget) || 0);
        return [
          item.name || "-",
          { content: formatRupeesPdf(pricePerQty), styles: { halign: "right" } },
          String(qty),
          { content: formatRupeesPdf(item.budget), styles: { halign: "right", fontStyle: "bold" } },
          item.notes || "-",
        ];
      });

      if (budgetRows.length === 0) {
        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(9.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("No budget allocation saved for this project.", marginX, cursorY + 4);
        cursorY += 24;
      } else {
        autoTable(docPdf, {
          ...baseTableOptions(),
          startY: cursorY,
          head: [["Item", "Price", "Quantity", "Total", "Notes"]],
          body: budgetRows,
          columnStyles: {
            0: { cellWidth: 140 },
            1: { cellWidth: 85, halign: "right" },
            2: { cellWidth: 65, halign: "center" },
            3: { cellWidth: 90, halign: "right" },
            4: { cellWidth: "auto" },
          },
          margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
        });
        cursorY = docPdf.lastAutoTable.finalY + 26;
      }

      // ---- Daily Material Logs ----
      if (cursorY > 640) {
        docPdf.addPage();
        cursorY = 96;
      }
      cursorY = drawSectionTitle(docPdf, "Daily Material Logs", marginX, cursorY);

      const logRows = sortedEntries.map((entry) => [
        formatDate(entry.entryDate),
        entry.name || "-",
        String(entry.quantity ?? "-"),
        entry.unit || "-",
        { content: formatRupeesPdf(entry.unitPrice), styles: { halign: "right" } },
        { content: formatRupeesPdf(entry.totalCost), styles: { halign: "right", fontStyle: "bold" } },
        entry.notes || "-",
      ]);

      if (logRows.length === 0) {
        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(9.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("No material logs recorded yet.", marginX, cursorY + 4);
        cursorY += 24;
      } else {
        autoTable(docPdf, {
          ...baseTableOptions(),
          startY: cursorY,
          styles: { ...baseTableOptions().styles, fontSize: 8.5 },
          head: [["Date", "Item", "Qty", "Unit", "Price", "Total", "Notes"]],
          body: logRows,
          columnStyles: {
            0: { cellWidth: 65 },
            1: { cellWidth: 110 },
            2: { cellWidth: 45, halign: "center" },
            3: { cellWidth: 55 },
            4: { cellWidth: 75, halign: "right" },
            5: { cellWidth: 80, halign: "right" },
            6: { cellWidth: "auto" },
          },
          margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
        });
        cursorY = docPdf.lastAutoTable.finalY + 26;
      }

      // ---- Logs Total ----
      if (cursorY > 660) {
        docPdf.addPage();
        cursorY = 96;
      }
      cursorY = drawSectionTitle(docPdf, "Logs Total", marginX, cursorY);

      docPdf.setDrawColor(...colors.borderLight);
      docPdf.setLineWidth(0.8);

      autoTable(docPdf, {
        startY: cursorY,
        theme: "plain",
        styles: {
          font: PDF_THEME.fontFamily,
          fontSize: 10.5,
          cellPadding: { top: 6, bottom: 6, left: 0, right: 0 },
          fontStyle: "bold",
          textColor: colors.primaryDark,
        },
        body: [["Total Logged Expense", formatRupeesPdf(totalActualPurchased)]],
        columnStyles: { 1: { halign: "right" } },
        margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
      });

      const pageCount = docPdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i += 1) {
        docPdf.setPage(i);
        const pageHeight = docPdf.internal.pageSize.getHeight();

        if (i > 1) {
          docPdf.setFont(PDF_THEME.fontFamily, "bold");
          docPdf.setFontSize(11);
          docPdf.setTextColor(...colors.primaryDark);
          docPdf.text(COMPANY_NAME, marginX, 30);

          docPdf.setFont(PDF_THEME.fontFamily, "normal");
          docPdf.setFontSize(9);
          docPdf.setTextColor(...colors.textMuted);
          docPdf.text(
            `Material Expense Report — ${clientName || "Client"}`,
            pageWidth - marginX,
            30,
            { align: "right" },
          );

          docPdf.setDrawColor(...colors.accentGold);
          docPdf.setLineWidth(1);
          docPdf.line(marginX, 44, pageWidth - marginX, 44);
        }

        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(8.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("Thank you for choosing Sarrathi Constructions.", marginX, pageHeight - 30);
        docPdf.text("This report is system generated.", marginX, pageHeight - 18);
        docPdf.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 18, {
          align: "right",
        });
      }

      docPdf.save(`${sanitizeFileName(clientName)}_Materials_Report.pdf`);
    } catch (err) {
      console.error("Failed to generate materials report PDF:", err);
      toast.error("Failed to generate materials report PDF.");
    } finally {
      setPdfGenerating(false);
    }
  };

  // =====================================================================
  // Generate PDF — Project Cost & Analytics Report (from the View Chart
  // modal). Reuses the exact same helpers/branding as
  // generateMaterialsPDF above (PDF_THEME, loadCompanyLogoDataUrl,
  // drawSectionTitle, baseTableOptions, formatRupeesPdf) and additionally
  // embeds the actual rendered Pie/Bar chart images (via each chart's
  // own canvas.toBase64Image()), plus a material-wise breakdown table,
  // full Daily Log breakdown, and payment info (kept strictly separate
  // from material expenditure — never added/subtracted together).
  // =====================================================================
  const [analyticsPdfGenerating, setAnalyticsPdfGenerating] = useState(false);

  const generateAnalyticsPDF = async () => {
    if (!invoiceRecord) return;

    setAnalyticsPdfGenerating(true);
    try {
      const { colors } = PDF_THEME;
      const docPdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = docPdf.internal.pageSize.getWidth();
      const marginX = 40;

      const logoDataUrl = await loadCompanyLogoDataUrl();
      const headerTop = 40;
      const logoSize = 54;

      if (logoDataUrl) {
        try {
          docPdf.addImage(logoDataUrl, "JPEG", marginX, headerTop, logoSize, logoSize);
        } catch (imgErr) {
          console.error("Failed to draw logo on analytics report PDF:", imgErr);
        }
      }

      const textX = marginX + logoSize + 16;
      const nameY = headerTop + logoSize / 2 - 6;

      docPdf.setFont(PDF_THEME.fontFamily, "bold");
      docPdf.setFontSize(17);
      docPdf.setTextColor(...colors.primaryDark);
      docPdf.text(COMPANY_NAME, textX, nameY);

      docPdf.setFont(PDF_THEME.fontFamily, "normal");
      docPdf.setFontSize(9);
      docPdf.setTextColor(...colors.textMuted);
            docPdf.text(COMPANY_ADDRESS, textX, nameY + 15, {
        maxWidth: pageWidth - marginX * 2 - logoSize - 16,
      });
      docPdf.text(`Phone: ${COMPANY_PHONE}`, textX, nameY + 27);
      docPdf.text(`Email: ${COMPANY_EMAIL}`, textX, nameY + 39);
      docPdf.text(`Website: ${COMPANY_WEBSITE}`, textX, nameY + 51);

      const logoRowBottom = Math.max(headerTop + logoSize, nameY + 51) + 16;
      docPdf.setDrawColor(...colors.accentGold);
      docPdf.setLineWidth(1.3);
      docPdf.line(marginX, logoRowBottom, pageWidth - marginX, logoRowBottom);

      const titleY = logoRowBottom + 26;

      docPdf.setFont(PDF_THEME.fontFamily, "bold");
      docPdf.setFontSize(15);
      docPdf.setTextColor(...colors.primaryDark);
      docPdf.text("PROJECT MATERIAL & EXPENDITURE REPORT", marginX, titleY);

      docPdf.setFont(PDF_THEME.fontFamily, "normal");
      docPdf.setFontSize(9.5);
      docPdf.setTextColor(...colors.textMuted);
      docPdf.text(
        `Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`,
        pageWidth - marginX,
        titleY,
        { align: "right" },
      );

      const headerBottom = titleY + 16;
      docPdf.setDrawColor(...colors.borderLight);
      docPdf.setLineWidth(0.8);
      docPdf.line(marginX, headerBottom, pageWidth - marginX, headerBottom);

      let cursorY = headerBottom + 22;

      // ---- Client & Project Information ----
      cursorY = drawSectionTitle(docPdf, "Client & Project Information", marginX, cursorY);

      autoTable(docPdf, {
        ...baseTableOptions(),
        startY: cursorY,
        showHead: false,
        styles: { ...baseTableOptions().styles, fontSize: 9.5 },
        body: [
          ["Client Name", clientName || "-", "Client ID", clientId || "-"],
          ["Project Type", projectType || "-", "Project Reference", getProjectRef(clientsById, clientDocId) || "-"],
        ],
        columnStyles: {
          0: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 100 },
          1: { cellWidth: 185 },
          2: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 100 },
          3: { cellWidth: "auto" },
        },
        margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
      });

      cursorY = docPdf.lastAutoTable.finalY + 26;

      // ---- Material Summary ----
      cursorY = drawSectionTitle(docPdf, "Material Summary", marginX, cursorY);

      autoTable(docPdf, {
        startY: cursorY,
        theme: "plain",
        styles: {
          font: PDF_THEME.fontFamily,
          fontSize: 11,
          cellPadding: { top: 4, bottom: 4, left: 0, right: 0 },
          fontStyle: "bold",
          textColor: colors.primaryDark,
        },
        body: [["Total Material Expenditure", formatRupeesPdf(totalMaterialExpenditure)]],
        columnStyles: { 1: { halign: "right" } },
        margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
      });

      cursorY = docPdf.lastAutoTable.finalY + 22;

      // ---- Material Distribution (Pie chart image + breakdown table) ----
      if (materialTotalsWithPct.length === 0) {
        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(9.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("No material data available for this project.", marginX, cursorY + 4);
        cursorY += 24;
      } else {
        cursorY = drawSectionTitle(docPdf, "Material Distribution", marginX, cursorY);

        if (pieChartRef.current) {
          try {
            const pieImg = pieChartRef.current.toBase64Image();
            const imgSize = 190;
            docPdf.addImage(pieImg, "PNG", marginX, cursorY, imgSize, imgSize);
          } catch (chartErr) {
            console.error("Failed to embed pie chart:", chartErr);
          }
        }

        const pieRows = materialTotalsWithPct.map((m) => [
          m.name,
          { content: formatRupeesPdf(m.total), styles: { halign: "right" } },
          { content: `${m.pct.toFixed(1)}%`, styles: { halign: "right" } },
        ]);

        autoTable(docPdf, {
          ...baseTableOptions(),
          startY: cursorY,
          head: [["Material", "Total Amount", "Percentage"]],
          body: pieRows,
          margin: { left: marginX + 210, right: marginX, top: PDF_HEADER_BAND },
          tableWidth: pageWidth - marginX * 2 - 210,
          columnStyles: {
            0: { cellWidth: "auto" },
            1: { cellWidth: 95, halign: "right" },
            2: { cellWidth: 75, halign: "right" },
          },
        });

        cursorY = Math.max(docPdf.lastAutoTable.finalY, cursorY + 190) + 26;
      }

      // ---- Daily Expenditure (Bar chart) ----
      if (cursorY > 560) {
        docPdf.addPage();
        cursorY = 44;
      }

      if (dailyTotals.length === 0) {
        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(9.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("No daily logs available for this project.", marginX, cursorY + 4);
        cursorY += 24;
      } else {
        cursorY = drawSectionTitle(docPdf, "Daily Expenditure", marginX, cursorY);

        if (barChartRef.current) {
          try {
            const barImg = barChartRef.current.toBase64Image();
            const imgWidth = pageWidth - marginX * 2;
            const imgHeight = 180;
            docPdf.addImage(barImg, "PNG", marginX, cursorY, imgWidth, imgHeight);
            cursorY += imgHeight + 20;
          } catch (chartErr) {
            console.error("Failed to embed bar chart:", chartErr);
          }
        }

        const dailyRows = dailyTotals.map((d) => [
          `Day ${d.dayNumber}`,
          formatDate(d.date),
          { content: formatRupeesPdf(d.total), styles: { halign: "right", fontStyle: "bold" } },
        ]);

        autoTable(docPdf, {
          ...baseTableOptions(),
          startY: cursorY,
          head: [["Day", "Date", "Day Total"]],
          body: dailyRows,
          margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
        });
        cursorY = docPdf.lastAutoTable.finalY + 26;
      }

      // ---- Payment Information (kept strictly separate — never added
      // to or subtracted from material expenditure) ----
      if (cursorY > 660) {
        docPdf.addPage();
        cursorY = 44;
      }
      cursorY = drawSectionTitle(docPdf, "Payment Information", marginX, cursorY);

      if (!invoiceRecord) {
        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(9.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("No payment information available for this project.", marginX, cursorY + 4);
        cursorY += 24;
      } else {
        autoTable(docPdf, {
          ...baseTableOptions(),
          startY: cursorY,
          theme: "grid",
          showHead: false,
          styles: { ...baseTableOptions().styles, fontSize: 9.5 },
          body: [
            ["Project Quotation", formatRupeesPdf(activeQuotation), "Amount Received", formatRupeesPdf(amountPaid)],
            ["Balance", formatRupeesPdf(activeQuotation - amountPaid), "", ""],
          ],
          columnStyles: {
            0: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 110 },
            1: { cellWidth: 145 },
            2: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 110 },
            3: { cellWidth: "auto" },
          },
          margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
        });
        cursorY = docPdf.lastAutoTable.finalY + 26;
      }

      // ---- Detailed Daily Logs (every log, not just the first few) ----
      if (dailyTotals.length > 0) {
        dailyTotals.forEach((day) => {
          if (cursorY > 640) {
            docPdf.addPage();
            cursorY = 44;
          }
          cursorY = drawSectionTitle(docPdf, `Day ${day.dayNumber} — ${formatDate(day.date)}`, marginX, cursorY);

          const rows = day.items.map((entry) => [
            entry.name || "-",
            String(entry.quantity ?? "-"),
            { content: formatRupeesPdf(entry.unitPrice), styles: { halign: "right" } },
            { content: formatRupeesPdf(entry.totalCost), styles: { halign: "right", fontStyle: "bold" } },
          ]);

          autoTable(docPdf, {
            ...baseTableOptions(),
            startY: cursorY,
            head: [["Material", "Qty", "Unit Price", "Total"]],
            body: rows,
            foot: [["", "", "Day Total", formatRupeesPdf(day.total)]],
            footStyles: { fillColor: colors.rowStripe, textColor: colors.primaryDark, fontStyle: "bold" },
            margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
          });
          cursorY = docPdf.lastAutoTable.finalY + 22;
        });
      }

      // ---- Footer (every page) ----
      const pageCount = docPdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        docPdf.setPage(i);
        const pageHeight = docPdf.internal.pageSize.getHeight();
        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(8.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("Thank you for choosing Sarrathi Constructions.", marginX, pageHeight - 30);
        docPdf.text("This report is system generated.", marginX, pageHeight - 18);
        docPdf.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 18, { align: "right" });
      }

      docPdf.save(`${sanitizeFileName(clientName)}_Project_Analytics_Report.pdf`);
    } catch (err) {
      console.error("Failed to generate analytics report PDF:", err);
      toast.error("Failed to generate analytics report PDF.");
    } finally {
      setAnalyticsPdfGenerating(false);
    }
  };

  // ---------------- Edit an existing budget-plan line item (unchanged) ----------------
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM_FORM);
  const [itemFormError, setItemFormError] = useState("");
  const [itemSaving, setItemSaving] = useState(false);

  const openEditItem = (index) => {
    const item = planItems[index];
    if (!item) return;

    setEditingIndex(index);
    setItemForm({
      name: item.name || "",
      pricePerQty: String(item.pricePerQty ?? item.budget ?? ""),
      quantity: String(item.quantity ?? ""),
      notes: item.notes || "",
    });
    setItemFormError("");
    setShowEditItemModal(true);
  };

  const closeEditItem = () => {
    if (itemSaving) return;
    setShowEditItemModal(false);
    setEditingIndex(null);
    setItemForm(EMPTY_ITEM_FORM);
    setItemFormError("");
  };

  const handleItemFormChange = (e) => {
    const { name, value } = e.target;

    if (name === "pricePerQty" || name === "quantity") {
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        setItemForm((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }

    setItemForm((prev) => ({ ...prev, [name]: value }));
    if (itemFormError) setItemFormError("");
  };

  const itemPreviewTotal =
    (Number(itemForm.pricePerQty) || 0) * (Number(itemForm.quantity) || 0);

  const saveItemEdit = async () => {
    if (editingIndex === null || !clientDocId || !budgetPlan) return;

    if (!itemForm.name.trim()) {
      setItemFormError("Material name is required.");
      return;
    }

    const updatedItems = planItems.map((item, idx) =>
      idx === editingIndex
        ? {
            ...item,
            name: itemForm.name.trim(),
            pricePerQty: Number(itemForm.pricePerQty) || 0,
            quantity: Number(itemForm.quantity) || 0,
            budget: itemPreviewTotal,
            notes: itemForm.notes || "",
          }
        : item,
    );

    const newTotalPlanned = updatedItems.reduce(
      (sum, item) => sum + (Number(item.budget) || 0),
      0,
    );

    if (activeQuotation > 0 && newTotalPlanned > activeQuotation) {
      setItemFormError(
        "This change would push the planned budget past the quotation amount.",
      );
      return;
    }

    setItemSaving(true);

    try {
      const planData = {
        ...budgetPlan,
        items: updatedItems,
        totalPlanned: newTotalPlanned,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "budgetPlans", clientDocId), planData);

      setBudgetPlan((prev) => ({
        ...prev,
        items: updatedItems,
        totalPlanned: newTotalPlanned,
      }));

      toast.success("Material Updated Successfully");
      closeEditItem();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update material.");
    } finally {
      setItemSaving(false);
    }
  };

  // ---------------- Add / Edit a daily material log entry ----------------
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null); // null = adding new
  const [entryForm, setEntryForm] = useState(EMPTY_ENTRY_FORM);
  const [entryFormError, setEntryFormError] = useState("");
  const [entrySaving, setEntrySaving] = useState(false);

  const openAddEntry = () => {
    setAddEntryDate(new Date().toISOString().slice(0, 10));
    setAddEntryRows([{ id: `row-${Date.now()}`, name: "", quantity: "", unitPrice: "", notes: "" }]);
    setAddEntryError("");
    setNewlyAddedEntryRowId(null);
    setShowAddEntryModal(true);
  };

  const openEditEntry = (entry) => {
    if (entry.source === "invoice") return; // logged from Invoice.js — read-only here
    setEditingEntryId(entry.id);
    setEntryForm({
      name: entry.name || "",
      quantity: String(entry.quantity ?? ""),
      unitPrice: String(entry.unitPrice ?? ""),
      notes: entry.notes || "",
      entryDate: entry.entryDate || new Date().toISOString().slice(0, 10),
    });
    setEntryFormError("");
    setShowEntryModal(true);
  };

  const closeEntryModal = () => {
    if (entrySaving) return;
    setShowEntryModal(false);
    setEditingEntryId(null);
    setEntryForm(EMPTY_ENTRY_FORM);
    setEntryFormError("");
  };

  const handleEntryFormChange = (e) => {
    const { name, value } = e.target;

    if ((name === "quantity" || name === "unitPrice") && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setEntryForm((prev) => ({ ...prev, [name]: value }));
    if (entryFormError) setEntryFormError("");
  };

  const entryPreviewTotal =
    (Number(entryForm.quantity) || 0) * (Number(entryForm.unitPrice) || 0);

  // Logging a material/labour expense is NEVER blocked — the client's
  // available balance can go negative and that's fine; it's simply shown
  // in red on the Remaining Payment indicator. No transaction-time
  // balance check happens here anymore.
  const saveEntry = async () => {
    const targetInvoiceId = invoiceRecord?.id;
    if (!targetInvoiceId) return;

    if (!entryForm.name.trim()) {
      setEntryFormError("Item name is required.");
      return;
    }
    if (!entryForm.quantity || Number(entryForm.quantity) <= 0) {
      setEntryFormError("Quantity must be greater than 0.");
      return;
    }
    if (entryForm.unitPrice === "" || Number(entryForm.unitPrice) < 0) {
      setEntryFormError("Unit price is required.");
      return;
    }

    setEntrySaving(true);

    const allocRef = doc(db, "paymentAllocations", targetInvoiceId);

    try {
      await runTransaction(db, async (transaction) => {
        const allocSnap = await transaction.get(allocRef);
        const allocData = allocSnap.exists() ? allocSnap.data() : {};
        const currentRawEntries = Array.isArray(allocData.materialEntries)
          ? allocData.materialEntries
          : [];

        let updatedEntries;

        if (editingEntryId) {
          updatedEntries = currentRawEntries.map((e) =>
            e.id === editingEntryId
              ? {
                  ...e,
                  name: entryForm.name.trim(),
                  quantity: Number(entryForm.quantity) || 0,
                  unitPrice: Number(entryForm.unitPrice) || 0,
                  totalCost: entryPreviewTotal,
                  notes: entryForm.notes || "",
                  entryDate: entryForm.entryDate,
                }
              : e,
          );
        } else {
          const newEntry = {
            id: `entry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: entryForm.name.trim(),
            quantity: Number(entryForm.quantity) || 0,
            unitPrice: Number(entryForm.unitPrice) || 0,
            totalCost: entryPreviewTotal,
            notes: entryForm.notes || "",
            entryDate: entryForm.entryDate,
            source: "materials",
            createdAt: new Date().toISOString(),
          };
          updatedEntries = [...currentRawEntries, newEntry];
        }

        transaction.set(
          allocRef,
          { materialEntries: updatedEntries, updatedAt: serverTimestamp() },
          { merge: true },
        );
      });

      await fetchMaterialEntries();
      toast.success(editingEntryId ? "Log Updated Successfully" : "Log Added Successfully");
      closeEntryModal();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to save material log.");
    } finally {
      setEntrySaving(false);
    }
  };

  // ---------------- Add Log Entry (multi-item) ----------------
  // Replaces the single-item "Add" flow above with a repeatable-row modal:
  // several materials can be entered together and submitted as one day's
  // log in a single Firestore write. Editing an existing entry (single
  // item) still uses the modal/state above via openEditEntry — unchanged.
  const [showAddEntryModal, setShowAddEntryModal] = useState(false);
  const [addEntryDate, setAddEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [addEntryRows, setAddEntryRows] = useState([
    { id: "row-0", name: "", quantity: "", unitPrice: "", notes: "" },
  ]);
  const [addEntryError, setAddEntryError] = useState("");
  const [addEntrySaving, setAddEntrySaving] = useState(false);

  // Scroll-to-new-item support (mirrors Estimation1.js's BOQ modal): tracks
  // the id of the most recently added row so the effect below can scroll
  // it into view and focus its Item Name input once rendered.
  const [newlyAddedEntryRowId, setNewlyAddedEntryRowId] = useState(null);
  const addEntryRowsScrollRef = useRef(null);
  const newEntryRowNameInputRef = useRef(null);

  const closeAddEntryModal = () => {
    if (addEntrySaving) return;
    setShowAddEntryModal(false);
    setAddEntryRows([{ id: "row-0", name: "", quantity: "", unitPrice: "", notes: "" }]);
    setAddEntryError("");
  };

  const addItemRow = () => {
    const newRowId = `row-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setAddEntryRows((prev) => [
      ...prev,
      { id: newRowId, name: "", quantity: "", unitPrice: "", notes: "" },
    ]);
    setNewlyAddedEntryRowId(newRowId);
  };

  const removeItemRow = (id) => {
    // No longer blocked at 1 remaining row — deleting the last item is
    // allowed, and the modal falls back to the "No items yet" empty state
    // (see log-entry-empty below) instead of forcing one row to stay.
    setAddEntryRows((prev) => prev.filter((r) => r.id !== id));
  };

  const updateItemRow = (id, field, value) => {
    if ((field === "quantity" || field === "unitPrice") && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }
    setAddEntryRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    if (addEntryError) setAddEntryError("");
  };

  const itemRowTotal = (row) => (Number(row.quantity) || 0) * (Number(row.unitPrice) || 0);

  const addEntryRowsTotal = addEntryRows.reduce((sum, row) => sum + itemRowTotal(row), 0);

  // Whenever a new row is added via "+ Add Item", scroll it into view
  // within the scrollable item list and focus its Item Name input, so the
  // admin can start typing immediately — same flow as before.
  useEffect(() => {
    if (!newlyAddedEntryRowId) return;

    const frame = requestAnimationFrame(() => {
      newEntryRowNameInputRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      newEntryRowNameInputRef.current?.focus();
      setNewlyAddedEntryRowId(null);
    });

    return () => cancelAnimationFrame(frame);
  }, [newlyAddedEntryRowId, addEntryRows]);

  // Saves every valid row (name + quantity > 0) together as one day's log,
  // in a single transaction — same `paymentAllocations/{invoiceId}
  // .materialEntries` array saveEntry() above writes to.
  const saveMultipleEntries = async () => {
    const targetInvoiceId = invoiceRecord?.id;
    if (!targetInvoiceId) return;

    const validRows = addEntryRows.filter((row) => row.name.trim() && Number(row.quantity) > 0);

    if (validRows.length === 0) {
      setAddEntryError("Add at least one item with a name and quantity.");
      return;
    }

    setAddEntrySaving(true);

    const allocRef = doc(db, "paymentAllocations", targetInvoiceId);

    try {
      await runTransaction(db, async (transaction) => {
        const allocSnap = await transaction.get(allocRef);
        const allocData = allocSnap.exists() ? allocSnap.data() : {};
        const currentRawEntries = Array.isArray(allocData.materialEntries)
          ? allocData.materialEntries
          : [];

        const newEntries = validRows.map((row, idx) => ({
          id: `entry-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
          name: row.name.trim(),
          quantity: Number(row.quantity) || 0,
          unit: "",
          unitPrice: Number(row.unitPrice) || 0,
          totalCost: itemRowTotal(row),
          supplier: "",
          notes: row.notes || "",
          entryDate: addEntryDate,
          source: "materials",
          createdAt: new Date().toISOString(),
        }));

        transaction.set(
          allocRef,
          { materialEntries: [...currentRawEntries, ...newEntries], updatedAt: serverTimestamp() },
          { merge: true },
        );
      });

      await fetchMaterialEntries();
      toast.success("Log Added Successfully");
      closeAddEntryModal();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to save material log.");
    } finally {
      setAddEntrySaving(false);
    }
  };

  // Lock the page's own scroll while any modal on this page is open. The
  // Add Log Entry modal can grow taller than the viewport once several
  // rows are added — without this, scrolling over it just scrolled the
  // page behind it instead of the modal's own content.
  useEffect(() => {
    const anyModalOpen = showEditItemModal || showEntryModal || showAddEntryModal || showChartModal;
    document.body.style.overflow = anyModalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showEditItemModal, showEntryModal, showAddEntryModal, showChartModal]);

  // ================= Render: Project picker view =================
  if (!clientDocId) {
    return (
      <div className="page-container materials-page">
        <div className="page-header">
          <div>
            <h1>Materials</h1>
            <p>Select a project to view its budget and material history.</p>
          </div>
        </div>

        <div className="materials-toolbar">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by client ID or name..."
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="materials-table-card">
          <table className="materials-table">
            <thead>
              <tr>
                <th>Client ID</th>
                <th>Client Name</th>
                <th>Project Type</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pickerLoading || clientsLoading ? (
                <tr>
                  <td colSpan="5" className="empty-row">
                    Loading projects...
                  </td>
                </tr>
             ) : filteredPickerRows.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-row">
                    No Projects With Budget Planning Found. Complete Budget Planning
                    from the Estimation page first.
                  </td>
                </tr>
              ) : (
                paginatedPickerRows.map((row) => (
                  <tr key={row.clientDocId}>
                    <td>
                      {row.clientId || "-"}
                      {getProjectRef(clientsById, row.clientDocId) && (
                        <span style={PROJECT_REF_BADGE_STYLE}>
                          {getProjectRef(clientsById, row.clientDocId)}
                        </span>
                      )}
                    </td>
                    <td>{row.name || "-"}</td>
                    <td>{row.projectType || "-"}</td>
                    <td>
                      <span className="status-pill">{row.status}</span>
                    </td>
                    <td>
                      <button
                        className="manage-materials-btn"
                        onClick={() => openMaterialsForClient(row)}
                      >
                        <FaBoxOpen />
                        Manage Materials
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination {...pickerPagination} />
        <ToastContainer toasts={toasts} onDismiss={toast.dismiss} />
      </div>
    );
  }

  // ================= Render: unknown / deleted client guard =================
  if (!clientsLoading && !clientsById[clientDocId]) {
    return (
      <div className="page-container materials-page">
        <div className="page-header">
          <div>
            <button className="back-link" onClick={() => navigate("/dashboard/materials")}>
              <FaArrowLeft /> All Projects
            </button>
            <h1>Project Not Found</h1>
            <p>This client no longer exists, so there's nothing to manage here.</p>
          </div>
        </div>
        <ToastContainer toasts={toasts} onDismiss={toast.dismiss} />
      </div>
    );
  }

  // ================= Render: Material tracker for a specific project =================
  const currentStatus = resolveClientStatus(clientsById, clientDocId, "In Talk");

  return (
    <div className="page-container materials-page">
      <div className="page-header">
        <div>
          <button className="back-link" onClick={() => navigate("/dashboard/materials")}>
            <FaArrowLeft /> All Projects
          </button>
          <h1>
            {clientName || "Materials"}
            {getProjectRef(clientsById, clientDocId) && (
              <span style={PROJECT_REF_BADGE_STYLE}>
                {getProjectRef(clientsById, clientDocId)}
              </span>
            )}
          </h1>
          <p>
            {clientId ? `${clientId} · ` : ""}
            {projectType || "Material budget management"}
            {" · "}
            <span className="status-pill materials-inline-status">{currentStatus}</span>
          </p>
        </div>

        <div className="materials-summary-card">
          <span className="materials-summary-label">Total Planned Budget</span>
          <span className="materials-summary-value">{formatRupees(plannedTotal)}</span>
        </div>
      </div>

      {/* ================= Top Actions — View Chart + Generate PDF ================= */}
      {invoiceRecord && (
        <div className="materials-actions-bar">
          <button
            className="manage-materials-btn"
            onClick={() => setShowChartModal(true)}
          >
            <FaChartBar />
            View Chart
          </button>
          <button
            className="generate-pdf-btn"
            onClick={generateMaterialsPDF}
            disabled={pdfGenerating}
            title="Download a Material Expense Report for this client"
          >
            <FaFilePdf />
            {pdfGenerating ? "Generating..." : "Generate PDF"}
          </button>
        </div>
      )}

      {/* ================= Material Items (Section A) ================= */}
      {budgetPlanLoading || estimationLoading ? (
        <div className="planned-budget-card">
          <div className="mb-empty">Loading budget plan...</div>
        </div>
      ) : !budgetPlan ? (
        <div className="planned-budget-card">
          <div className="mb-empty">
            No budget plan found for this client yet. Please complete Budget Planning
            from the Estimation page first.
          </div>
        </div>
      ) : (
        <div className="planned-budget-card">
          <div className="planned-budget-header">
            <div>
              <h3>
                <FaWallet className="planned-budget-icon" /> Budget Allocation
              </h3>
              <span className="planned-budget-hint">
                Material Breakdown from Estimation's Budget Planning — categories are
                added or removed there; edit individual allocations here. Payment
                history and the Final Quotation now live on the Payments page.
              </span>
            </div>
          </div>

          <div className="planned-budget-summary">
            {hasFinalQuotation ? (
              <div className="planned-budget-chip">
                <span className="view-label">Final Quotation</span>
                <span className="planned-budget-value">{formatRupees(finalQuotation)}</span>
              </div>
            ) : (
              <div className="planned-budget-chip">
                <span className="view-label">Estimated Quotation</span>
                <span className="planned-budget-value">{formatRupees(estimatedQuotation)}</span>
              </div>
            )}
            <div className="planned-budget-chip">
              <span className="view-label">Total Planned Budget</span>
              <span className="planned-budget-value">{formatRupees(plannedTotal)}</span>
            </div>
            <div
              className={`planned-budget-chip${plannedRemaining < 0 ? " over-budget" : ""}`}
            >
              <span className="view-label">Remaining Budget</span>
              <span className="planned-budget-value">{formatRupees(plannedRemaining)}</span>
            </div>
          </div>

          <div className="mb-table">
            <div className="mb-row mb-row-head">
              <span>Material</span>
              <span>Unit Price</span>
              <span>Quantity</span>
              <span>Budget Amount</span>
              <span>Notes</span>
              <span aria-hidden="true"></span>
            </div>

            <div className="mb-rows-scroll">
              {planItems.length === 0 ? (
                <div className="mb-empty">No line items in this budget plan.</div>
              ) : (
                planItems.map((item, idx) => {
                  const qty = item.quantity ?? 1;
                  const pricePerQty =
                    item.pricePerQty ??
                    (qty ? (Number(item.budget) || 0) / qty : Number(item.budget) || 0);
                  return (
                    <div className="mb-row" key={`${item.name}-${idx}`}>
                      <span className="mb-static">{item.name}</span>
                      <span className="mb-static">{formatRupees(pricePerQty)}</span>
                      <span className="mb-static">{qty}</span>
                      <span className="mb-total">{formatRupees(item.budget)}</span>
                      <span className="mb-static">{item.notes || "-"}</span>
                      <button
                        className="icon-btn edit-icon"
                        title="Edit Material"
                        onClick={() => openEditItem(idx)}
                      >
                        <FaEdit />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================= Financial Summary (Final Quotation / Total
          Received / Total Spent / Remaining Funds) =================
          Replaces the old Planned Budget / Actual Purchased / Remaining
          Planned / Remaining Payment cards. All four values are derived
          from data already scoped to THIS project's invoiceRecord.id —
          Final Quotation from activeQuotation (prioritizes the Invoice/
          Payments Final Quotation over the pre-invoice BOQ estimation),
          Total Received from invoiceRecord.amountPaid (the actual
          payment-history sum), Total Spent from every daily material
          log entry's totalCost, and Remaining Funds as
          Total Received - Total Spent — never clamped, and shown in red
          via the existing over-budget style when negative. */}
      {invoiceRecord && (
        <div className="planned-budget-card budget-vs-actual-card">
          <div className="planned-budget-header">
            <div>
              <h3>
                <FaChartPie className="planned-budget-icon" /> Budget vs Actual
              </h3>
              <span className="planned-budget-hint">
                The project's Final Quotation, total received from the client, total
                spent through Daily Material Logs, and the funds remaining —
                Remaining Funds = Total Received − Total Spent.
              </span>
            </div>
          </div>

          <div className="planned-budget-summary bva-summary">
            <div className="planned-budget-chip">
              <span className="view-label">Final Quotation</span>
              <span className="planned-budget-value">{formatRupees(activeQuotation)}</span>
            </div>
            <div className="planned-budget-chip">
              <span className="view-label">Total Received</span>
              <span className="planned-budget-value">{formatRupees(amountPaid)}</span>
            </div>
            <div className="planned-budget-chip">
              <span className="view-label">Total Spent</span>
              <span className="planned-budget-value">{formatRupees(totalActualPurchased)}</span>
            </div>
            <div
              className={`planned-budget-chip${remainingPayment < 0 ? " over-budget" : ""}`}
              title="Total Received minus Total Spent. Can go negative — logging is never blocked."
            >
              <span className="view-label">Remaining Funds</span>
              <span className="planned-budget-value">{formatSignedRupees(remainingPayment)}</span>
            </div>
          </div>
        </div>
      )}

      {/* ================= Daily Material Logs ================= */}
      <div className="payment-history-section">
        <div className="planned-budget-header payment-history-header">
          <div>
            <h3>
              <FaBoxOpen className="planned-budget-icon" /> Daily Material Logs
            </h3>
            <span className="planned-budget-hint">
              A single running record of every construction expense — materials,
              labour, or anything else — grouped by day. Logging an entry is never
              blocked, even if it pushes Remaining Payment into the negative.
            </span>
          </div>
          {invoiceRecord && (
            <button className="manage-materials-btn" onClick={openAddEntry}>
              <FaPlus />
              Add Log Entry
            </button>
          )}
        </div>

        {invoiceLoading || entriesLoading ? (
          <div className="planned-budget-card">
            <div className="mb-empty">Loading material logs...</div>
          </div>
        ) : !invoiceRecord ? (
          <div className="planned-budget-card">
            <div className="mb-empty">
              This project hasn't reached Invoice yet — log material entries here
              once this project has an invoice.
            </div>
          </div>
        ) : dailyLogGroups.length === 0 ? (
          <div className="planned-budget-card">
            <div className="mb-empty">No material logs recorded yet.</div>
          </div>
        ) : (
          dailyLogGroups.map((group) => (
            <div className="planned-budget-card payment-ledger-card" key={group.date}>
              <div
                className="payment-card-header day-toggle-header"
                onClick={() => toggleDayExpanded(group.date)}
              >
                <div className="payment-card-title">
                  <span className="day-toggle-arrow">
                    {isDayExpanded(group.date) ? (
                      <FaChevronDown title="Collapse" />
                    ) : (
                      <FaChevronRight title="Expand" />
                    )}
                  </span>
                  <span className="payment-card-number">Day {group.dayNumber}</span>
                  <span className="payment-card-date">{formatDate(group.date)}</span>
                </div>
              </div>

              {isDayExpanded(group.date) && (
                <div className="mb-table alloc-table">
                  <div className="mb-row ledger-row mb-row-head">
                    <span>Item</span>
                    <span>Qty / Unit</span>
                    <span>Unit Price</span>
                    <span>Total</span>
                    <span>Supplier</span>
                    <span>Notes</span>
                    <span aria-hidden="true"></span>
                  </div>

                  <div className="mb-rows-scroll">
                    {group.items.map((entry) => (
                      <div className="mb-row ledger-row" key={entry.id}>
                        <span className="mb-static">
                          {entry.name}
                          {entry.source === "invoice" && (
                            <span
                              className="alloc-chip alloc-chip-legacy"
                              title="Logged from the Invoice page"
                            >
                              From Invoice
                            </span>
                          )}
                        </span>
                        <span className="mb-static">
                          {entry.quantity} {entry.unit || ""}
                        </span>
                        <span className="mb-static">{formatRupees(entry.unitPrice)}</span>
                        <span className="mb-total">{formatRupees(entry.totalCost)}</span>
                        <span className="mb-static">{entry.supplier || "-"}</span>
                        <span className="mb-static">{entry.notes || "-"}</span>
                        <button
                          className="icon-btn edit-icon"
                          title={
                            entry.source === "invoice"
                              ? "Logged from the Invoice page (read-only here)"
                              : "Edit Log"
                          }
                          onClick={() => openEditEntry(entry)}
                          disabled={entry.source === "invoice"}
                        >
                          <FaEdit />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Edit Budget-Plan Line Item Modal (unchanged) */}
      <AnimatePresence>
        {showEditItemModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="materials-page-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Edit Material Allocation</h2>

              <div className="form-grid materials-edit-form">
                <input
                  type="text"
                  name="name"
                  placeholder="Material Name"
                  value={itemForm.name}
                  onChange={handleItemFormChange}
                />

                <input
                  type="text"
                  inputMode="decimal"
                  name="pricePerQty"
                  placeholder="Price Per Qty (₹)"
                  value={itemForm.pricePerQty}
                  onChange={handleItemFormChange}
                />

                <input
                  type="text"
                  inputMode="decimal"
                  name="quantity"
                  placeholder="Quantity"
                  value={itemForm.quantity}
                  onChange={handleItemFormChange}
                />

                <input
                  type="text"
                  name="notes"
                  placeholder="Optional note"
                  value={itemForm.notes}
                  onChange={handleItemFormChange}
                />
              </div>

              <div className="materials-edit-total">
                <span className="view-label">Total (auto-calculated)</span>
                <span className="balance-amount-value">{formatRupees(itemPreviewTotal)}</span>
              </div>

              {itemFormError && (
                <span className="field-error materials-form-error">{itemFormError}</span>
              )}

              <div className="modal-buttons">
                <button className="cancel-btn" disabled={itemSaving} onClick={closeEditItem}>
                  Cancel
                </button>
                <button className="save-btn" disabled={itemSaving} onClick={saveItemEdit}>
                  {itemSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit Daily Material Log Modal */}
      <AnimatePresence>
        {showEntryModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="materials-page-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>{editingEntryId ? "Edit Log Entry" : "Add Log Entry"}</h2>

              <div className="form-grid materials-edit-form">
                <input
                  type="text"
                  name="name"
                  placeholder="Item Name (e.g. Cement, Labour)"
                  value={entryForm.name}
                  onChange={handleEntryFormChange}
                />

                <input
                  type="text"
                  inputMode="decimal"
                  name="quantity"
                  placeholder="Quantity"
                  value={entryForm.quantity}
                  onChange={handleEntryFormChange}
                />

                <input
                  type="text"
                  inputMode="decimal"
                  name="unitPrice"
                  placeholder="Price Per Unit (₹)"
                  value={entryForm.unitPrice}
                  onChange={handleEntryFormChange}
                />

                <input
                  type="date"
                  name="entryDate"
                  value={entryForm.entryDate}
                  onChange={handleEntryFormChange}
                />

                <input
                  type="text"
                  name="notes"
                  placeholder="Optional note"
                  value={entryForm.notes}
                  onChange={handleEntryFormChange}
                />
              </div>

              <div className="materials-edit-total">
                <span className="view-label">Total Cost (auto-calculated)</span>
                <span className="balance-amount-value">{formatRupees(entryPreviewTotal)}</span>
              </div>

              <div className="alloc-mode-note">
                Logs are never blocked, even if this pushes Remaining Payment negative —
                it will simply show in red as a heads-up that expenses have outpaced
                payments received so far.
              </div>

              {entryFormError && (
                <span className="field-error materials-form-error">{entryFormError}</span>
              )}

              <div className="modal-buttons">
                <button className="cancel-btn" disabled={entrySaving} onClick={closeEntryModal}>
                  Cancel
                </button>
                <button className="save-btn" disabled={entrySaving} onClick={saveEntry}>
                  {entrySaving ? "Saving..." : "Save Log"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Log Entry Modal — multiple items entered together and submitted
          as one day's log. Editing a single existing entry still uses the
          modal above via openEditEntry/showEntryModal.

          UI is a direct replica of Estimation1.js's BOQ (.budget-modal-v2 /
          .bmv2-*) modal — same header/close-X, same summary-chip bar, same
          6-column Items/Quantity/Price/Total/Notes/delete row grid, same
          fixed-height scrollable rows area, same dashed Add Item bar.
          Classes are prefixed "lev2-" (defined in Materials.css) so they
          never collide with bmv2-* or anything else, even though the CSS
          rules themselves are copied 1:1 from the BOQ modal. */}
      <AnimatePresence>
        {showAddEntryModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="lev2-modal"
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <div className="lev2-header">
                <div>
                  <h2>Add Log Entry</h2>
                  <p className="lev2-subtitle">
                    Log one or more construction expenses for a single day — the total
                    below becomes this day's logged expense.
                  </p>
                </div>
                <button
                  type="button"
                  className="lev2-close"
                  disabled={addEntrySaving}
                  onClick={closeAddEntryModal}
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="lev2-summary">
                <div className="lev2-chip">
                  <span className="lev2-chip-label">Date</span>
                  <input
                    type="date"
                    className="lev2-date-input"
                    value={addEntryDate}
                    onChange={(e) => setAddEntryDate(e.target.value)}
                  />
                </div>
                <div className="lev2-chip">
                  <span className="lev2-chip-label">Total</span>
                  <span className="lev2-chip-value">{formatRupees(addEntryRowsTotal)}</span>
                </div>
              </div>

              <div className="lev2-rows">
                <div className="lev2-row lev2-row-head">
                  <span>Items</span>
                  <span>Quantity</span>
                  <span>Price</span>
                  <span>Total</span>
                  <span>Notes</span>
                  <span aria-hidden="true"></span>
                </div>

                <div className="lev2-rows-scroll" ref={addEntryRowsScrollRef}>
                  <AnimatePresence initial={false}>
                    {addEntryRows.map((row) => (
                      <motion.div
                        key={row.id}
                        className="lev2-row"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                      >
                        <input
                          type="text"
                          className="lev2-input"
                          placeholder="Item name"
                          value={row.name}
                          ref={row.id === newlyAddedEntryRowId ? newEntryRowNameInputRef : undefined}
                          onChange={(e) => updateItemRow(row.id, "name", e.target.value)}
                        />
                        <input
                          type="text"
                          inputMode="decimal"
                          className="lev2-input lev2-input-qty"
                          placeholder="0"
                          value={row.quantity}
                          onChange={(e) => updateItemRow(row.id, "quantity", e.target.value)}
                        />
                        <input
                          type="text"
                          inputMode="decimal"
                          className="lev2-input"
                          placeholder="₹0"
                          value={row.unitPrice}
                          onChange={(e) => updateItemRow(row.id, "unitPrice", e.target.value)}
                        />
                        <span className="lev2-total">{formatRupees(itemRowTotal(row))}</span>
                        <input
                          type="text"
                          className="lev2-input"
                          placeholder="Optional note"
                          value={row.notes}
                          onChange={(e) => updateItemRow(row.id, "notes", e.target.value)}
                        />
                        <button
                          type="button"
                          className="lev2-row-delete"
                          title="Remove item"
                          onClick={() => removeItemRow(row.id)}
                        >
                          <FaTrash />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {addEntryRows.length === 0 && (
                    <div className="lev2-empty">
                      No items yet — use "Add Item" below to start logging.
                    </div>
                  )}
                </div>
              </div>

              <button type="button" className="lev2-add-item" onClick={addItemRow}>
                <FaPlus /> Add Item
              </button>

              <p className="lev2-note">
                Logs are never blocked, even if this pushes Remaining Funds negative —
                it will simply show in red as a heads-up that expenses have outpaced
                payments received so far.
              </p>

              {addEntryError && (
                <span className="field-error lev2-error">{addEntryError}</span>
              )}

              <div className="modal-buttons">
                <button className="cancel-btn" disabled={addEntrySaving} onClick={closeAddEntryModal}>
                  Cancel
                </button>
                <button className="save-btn" disabled={addEntrySaving} onClick={saveMultipleEntries}>
                  {addEntrySaving ? "Saving..." : "Save Log"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= View Chart — Analytics Modal ================= */}
      <AnimatePresence>
        {showChartModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="chart-modal"
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <div className="chart-modal-header">
                <div>
                  <h2>
                    Project Analytics — {clientName || "Client"}
                    {getProjectRef(clientsById, clientDocId) && (
                      <span style={PROJECT_REF_BADGE_STYLE}>
                        {getProjectRef(clientsById, clientDocId)}
                      </span>
                    )}
                  </h2>
                  <p className="chart-modal-subtitle">
                    Material-wise expenditure distribution and daily spending, based on
                    this project's Daily Material Logs.
                  </p>
                </div>
                <button
                  type="button"
                  className="lev2-close"
                  onClick={() => setShowChartModal(false)}
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="chart-modal-body">
                {/* Summary chips */}
                <div className="lev2-summary">
                  <div className="lev2-chip">
                    <span className="lev2-chip-label">Total Material Expenditure</span>
                    <span className="lev2-chip-value">
                      {formatRupees(totalMaterialExpenditure)}
                    </span>
                  </div>
                  <div className="lev2-chip">
                    <span className="lev2-chip-label">Total Daily Logs</span>
                    <span className="lev2-chip-value">{dailyTotals.length}</span>
                  </div>
                </div>

                {(entriesLoading || invoiceLoading) ? (
                  <div className="mb-empty">Loading analytics...</div>
                ) : (
                  <>
                    {/* Pie chart — material distribution */}
                    <div className="chart-card">
                      <h3 className="chart-card-title">
                        <FaChartPie /> Material Distribution
                      </h3>
                      {materialTotalsWithPct.length === 0 ? (
                        <div className="mb-empty">No material data available for this project.</div>
                      ) : (
                        <div className="chart-card-canvas chart-card-canvas-pie">
                          <Pie ref={pieChartRef} data={pieChartData} options={pieChartOptions} />
                        </div>
                      )}
                    </div>

                    {/* Bar chart — daily expenditure */}
                    <div className="chart-card">
                      <h3 className="chart-card-title">
                        <FaChartBar /> Daily Expenditure
                      </h3>
                      {dailyTotals.length === 0 ? (
                        <div className="mb-empty">No daily logs available for this project.</div>
                      ) : (
                        <>
                          <div className="chart-card-canvas chart-card-canvas-bar">
                            <Bar ref={barChartRef} data={barChartData} options={barChartOptions} />
                          </div>

                          {/* Material breakdown per day — table view, so the
                              breakdown is never hidden even when the bar
                              chart itself only shows day totals. */}
                          <div className="chart-daily-breakdown">
                            {dailyTotals.map((day) => (
                              <div className="chart-daily-breakdown-row" key={day.date}>
                                <span className="chart-daily-breakdown-day">
                                  Day {day.dayNumber} ({formatDate(day.date)})
                                </span>
                                <div className="chart-daily-breakdown-items">
                                  {day.items.map((item) => (
                                    <span className="chart-daily-breakdown-item" key={item.id}>
                                      {item.name}: {formatRupees(item.totalCost)}
                                    </span>
                                  ))}
                                </div>
                                <span className="chart-daily-breakdown-total">
                                  {formatRupees(day.total)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Payment info — kept strictly separate from material
                        expenditure. */}
                    {invoiceRecord && (
                      <div className="chart-card">
                        <h3 className="chart-card-title">
                          <FaWallet /> Payment Information
                        </h3>
                        <div className="planned-budget-summary">
                          <div className="planned-budget-chip">
                            <span className="view-label">Project Quotation</span>
                            <span className="planned-budget-value">{formatRupees(activeQuotation)}</span>
                          </div>
                          <div className="planned-budget-chip">
                            <span className="view-label">Amount Received</span>
                            <span className="planned-budget-value">{formatRupees(amountPaid)}</span>
                          </div>
                          <div className="planned-budget-chip">
                            <span className="view-label">Balance</span>
                            <span className="planned-budget-value">
                              {formatRupees(activeQuotation - amountPaid)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-buttons">
                <button className="cancel-btn" onClick={() => setShowChartModal(false)}>
                  Close
                </button>
                <button
                  className="save-btn"
                  disabled={analyticsPdfGenerating}
                  onClick={generateAnalyticsPDF}
                >
                  <FaFilePdf />
                  {analyticsPdfGenerating ? "Generating..." : "Generate PDF"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer toasts={toasts} onDismiss={toast.dismiss} />
    </div>
  );
}

export default Materials;